import { datadogRum } from "@datadog/browser-rum";
import { useContext } from "react";

import { OptIn } from "@btnet/services-btnet/handlers/models/optIn";
import { useUsersOptInPostV2 } from "@btnet/services-btnet/handlers/users/users";
import { mobile } from "@btnet/utilities-webview";

import { globalInfoQueryKey } from "helpers/AppProvider.api.handler";
import type { GlobalInfo } from "helpers/AppProvider.types";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { queryClient } from "helpers/queryClient";

import { BTLocalStorage } from "types/btStorage";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { cloneInstance } from "utilities/object/object";

/**
 * Reads and writes the user's Research Preview (information-architecture) opt-in decision.
 *
 * State is `userInfo.optIns.infoArch` from `UserInfoContext` — the global-info payload that
 * already loads on page boot. The tri-state is collapsed to a boolean here: only an explicit
 * `true` counts as opted in; `null` (never decided) and `false` (opted out) both render the
 * v1 app. Writes go through `POST /v2/users/opt-in` and optimistically patch the global-info
 * query cache, which is what `AppProvider` renders `UserInfoContext` from — so a new decision
 * propagates to every context consumer (including `AppRoutes`' opt-in branch, which swaps the
 * whole app on opt-out) without a full refetch. The decision is per-person, so it follows the
 * user across devices and builders.
 *
 * A successful write also emits the `infoArchOptIn` Datadog RUM action. That lives here rather
 * than in a caller so every consumer of this hook is instrumented by construction — the shell's
 * own opt-out path included.
 */
export const useInfoArchOptIn = () => {
    const userInfo = useContext(UserInfoContext);
    const isOptedIn = userInfo?.optIns?.infoArch === true;

    const { mutate: setOptIn, isLoading: isPending } = useUsersOptInPostV2({
        mutation: {
            onSuccess: (_, variables) => {
                // Optimistically update the global context cache so the UI reflects the new
                // opt-in without a full global-info refetch. This avoids a MainNavigation reload
                // that causes menu flicker. Use setQueriesData with a partial key because
                // useGetGlobalInfoQuery keys as [globalInfoQueryKey, handler.cacheKey].
                // Preserve class prototypes by shallow-copying via Object.create instead of
                // spreading into plain literals.
                queryClient.setQueriesData<GlobalInfo>([globalInfoQueryKey], (old) => {
                    if (!old?.userInfo) return old;
                    const newUserInfo = cloneInstance(old.userInfo, {
                        optIns: {
                            ...old.userInfo.optIns,
                            [OptIn.InfoArch]: variables.data.optedIn,
                        },
                    });
                    return cloneInstance(old, { userInfo: newUserInfo });
                });

                // Datadog RUM, in addition to the ValueChange event BTSwitch already sends to
                // Mixpanel — that tracking never reaches Datadog, so without this the decision is
                // invisible to the Datadog board. `optedIn` lands as `@context.optedIn` and is the
                // only property the board splits the two directions by.
                //
                // Fires on success rather than on the click, so a failed POST records nothing.
                // BTSwitch's own Mixpanel event still fires on the interaction, so the two vendors
                // disagree slightly by design: Mixpanel counts attempts, Datadog counts writes.
                const optedIn = variables.data.optedIn;
                const optInTime = BTLocalStorage.get("bt-number-infoArchOptedInAt");

                datadogRum.addAction("infoArchOptIn", {
                    optedIn,
                    surface: mobile.isWebview ? "webview" : "browser",
                    pagePath: window.location.pathname,
                    // Only meaningful when the matching opt-in happened in this same browser —
                    // localStorage is per-device, so a cross-device reversal omits it rather than
                    // reporting a wrong duration.
                    ...(!optedIn && optInTime !== null
                        ? { msInExperience: Date.now() - optInTime }
                        : {}),
                });

                BTLocalStorage.set("bt-number-infoArchOptedInAt", optedIn ? Date.now() : null);
            },
            onError: (error) => showAPIErrorMessage(error),
        },
    });

    const setOptedIn = (optedIn: boolean) => setOptIn({ data: { optIn: OptIn.InfoArch, optedIn } });

    return { isOptedIn, isPending, setOptedIn };
};
